import { useState, useRef, useEffect } from "react";
import { Sparkles, Send, RefreshCw, AlertCircle, Loader2, TrendingUp, FileText } from "lucide-react";
import api from "../lib/api";
import { formatRupiah } from "../lib/format";

const TABS = [
    { v: "chat", l: "Tanya AI", icon: Sparkles },
    { v: "prediksi", l: "Prediksi Penghasilan", icon: TrendingUp },
    { v: "ringkasan", l: "Ringkasan Performa", icon: FileText },
];

const CONTOH_PERTANYAAN = [
    "Vendor mana yang paling banyak bon bulan ini?",
    "Berapa total setoran minggu ini?",
    "Siapa yang hampir sampai batas plafon kasbon?",
    "Berapa reject menunggu perbaikan di semua vendor?",
];

export function AsistenAIPanel() {
    const [tab, setTab] = useState("chat");
    return (
        <div className="panel border-indigo-200 bg-indigo-50/30 mb-6" data-testid="panel-asisten-ai">
            <div className="flex items-center gap-2 p-3 border-b border-indigo-100">
                <Sparkles className="w-4 h-4 text-indigo-600" />
                <h2 className="font-semibold text-sm text-indigo-900">Asisten AI <span className="text-[10px] font-normal text-indigo-500 ml-1">(Beta · gpt-4o-mini)</span></h2>
            </div>
            <div className="flex gap-1 p-2 border-b border-indigo-100 bg-white">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    const active = tab === t.v;
                    return (
                        <button
                            key={t.v}
                            onClick={() => setTab(t.v)}
                            data-testid={`ai-tab-${t.v}`}
                            className={`px-3 py-1.5 text-xs font-medium rounded inline-flex items-center gap-1.5 transition-colors ${
                                active ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-indigo-100"
                            }`}
                        >
                            <Icon className="w-3.5 h-3.5" /> {t.l}
                        </button>
                    );
                })}
            </div>
            <div className="p-4 bg-white">
                {tab === "chat" && <ChatBox />}
                {tab === "prediksi" && <PrediksiTab />}
                {tab === "ringkasan" && <RingkasanTab />}
            </div>
        </div>
    );
}

function ChatBox() {
    const [messages, setMessages] = useState([]); // {role, content}
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, loading]);

    const send = async (msg) => {
        const text = (msg ?? input).trim();
        if (!text || loading) return;
        setErr("");
        setInput("");
        const nextMsgs = [...messages, { role: "user", content: text }];
        setMessages(nextMsgs);
        setLoading(true);
        try {
            const r = await api.post("/ai/chat", { message: text, history: messages });
            setMessages([...nextMsgs, { role: "assistant", content: r.data.answer }]);
        } catch (e) {
            setErr(e.response?.data?.detail || "AI gagal menjawab");
            setMessages([...nextMsgs, { role: "assistant", content: "⚠ " + (e.response?.data?.detail || "Gagal") }]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <div
                ref={scrollRef}
                data-testid="ai-chat-log"
                className="h-64 overflow-y-auto border border-slate-200 rounded-lg p-3 mb-3 bg-slate-50 space-y-2 text-sm"
            >
                {messages.length === 0 && (
                    <div className="text-slate-400 italic text-xs py-6 text-center">
                        <Sparkles className="w-4 h-4 inline mr-1" />
                        Tanyakan apa saja tentang data vendor Anda. Contoh:
                        <div className="mt-3 space-y-1.5 not-italic">
                            {CONTOH_PERTANYAAN.map((q) => (
                                <button
                                    key={q}
                                    onClick={() => send(q)}
                                    data-testid={`ai-contoh-${q.slice(0, 10)}`}
                                    className="block mx-auto px-3 py-1 text-xs text-indigo-700 hover:bg-indigo-50 rounded-full border border-indigo-200"
                                >
                                    {q}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {messages.map((m, i) => (
                    <div
                        key={i}
                        className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                        data-testid={`ai-msg-${m.role}-${i}`}
                    >
                        <div
                            className={`max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                                m.role === "user"
                                    ? "bg-indigo-600 text-white"
                                    : "bg-white border border-slate-200 text-slate-800"
                            }`}
                        >
                            {m.content}
                        </div>
                    </div>
                ))}
                {loading && (
                    <div className="flex justify-start" data-testid="ai-typing">
                        <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-500 inline-flex items-center gap-2">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> AI sedang mengetik...
                        </div>
                    </div>
                )}
            </div>
            {err && (
                <div className="text-xs text-red-600 mb-2 flex items-center gap-1" data-testid="ai-chat-error">
                    <AlertCircle className="w-3 h-3" /> {err}
                </div>
            )}
            <div className="flex gap-2">
                <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                    placeholder="Tulis pertanyaan..."
                    disabled={loading}
                    data-testid="ai-chat-input"
                    className="flex-1 border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:bg-slate-50"
                />
                <button
                    onClick={() => send()}
                    disabled={loading || !input.trim()}
                    data-testid="ai-chat-send"
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white rounded text-sm font-medium inline-flex items-center gap-1.5"
                >
                    <Send className="w-3.5 h-3.5" /> Kirim
                </button>
            </div>
        </div>
    );
}

function PrediksiTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const load = async () => {
        setLoading(true); setErr("");
        try {
            const r = await api.get("/ai/prediksi-penghasilan");
            setData(r.data);
        } catch (e) {
            setErr(e.response?.data?.detail || "Gagal mengambil prediksi");
        } finally { setLoading(false); }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <div className="text-xs text-slate-600">
                    AI memprediksi penghasilan bulan ini per vendor berdasar tren 6 bulan terakhir.
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    data-testid="ai-prediksi-refresh"
                    className="px-3 py-1.5 border border-indigo-600 text-indigo-700 hover:bg-indigo-50 rounded text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                    {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    {loading ? "Memproses..." : "Refresh Prediksi"}
                </button>
            </div>
            {err && (
                <div className="text-xs text-red-600 mb-2 flex items-center gap-1" data-testid="ai-prediksi-error">
                    <AlertCircle className="w-3 h-3" /> {err}
                </div>
            )}
            {data?.note && <div className="text-xs text-slate-500 italic py-6 text-center">{data.note}</div>}
            {data?.prediksi && data.prediksi.length > 0 && (
                <div className="overflow-x-auto">
                    <table className="table-nota" data-testid="ai-prediksi-table">
                        <thead>
                            <tr>
                                <th>Vendor</th>
                                <th className="text-right">Prediksi Penghasilan</th>
                                <th>Basis Perhitungan</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.prediksi.map((p, i) => (
                                <tr key={i}>
                                    <td className="font-medium">{p.nama_vendor || "-"}</td>
                                    <td className="num num-kredit">{formatRupiah(p.prediksi_rp || 0)}</td>
                                    <td className="text-xs text-slate-600">{p.basis_perhitungan_singkat || "-"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {!data && !loading && !err && (
                <div className="text-center py-6 text-xs text-slate-400 italic">
                    Klik <b>Refresh Prediksi</b> untuk mulai.
                </div>
            )}
        </div>
    );
}

function RingkasanTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const load = async () => {
        setLoading(true); setErr("");
        try {
            const r = await api.get("/ai/ringkasan-vendor");
            setData(r.data);
        } catch (e) {
            setErr(e.response?.data?.detail || "Gagal mengambil ringkasan");
        } finally { setLoading(false); }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <div className="text-xs text-slate-600">
                    Ringkasan performa setiap vendor bulan ini, ditulis dalam kalimat natural.
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    data-testid="ai-ringkasan-refresh"
                    className="px-3 py-1.5 border border-indigo-600 text-indigo-700 hover:bg-indigo-50 rounded text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                    {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    {loading ? "Memproses..." : "Refresh Ringkasan"}
                </button>
            </div>
            {err && (
                <div className="text-xs text-red-600 mb-2 flex items-center gap-1" data-testid="ai-ringkasan-error">
                    <AlertCircle className="w-3 h-3" /> {err}
                </div>
            )}
            {data?.note && <div className="text-xs text-slate-500 italic py-6 text-center">{data.note}</div>}
            {data?.ringkasan && data.ringkasan.length > 0 && (
                <ul className="space-y-2" data-testid="ai-ringkasan-list">
                    {data.ringkasan.map((r, i) => (
                        <li
                            key={i}
                            className="border border-slate-200 rounded-lg p-3 bg-slate-50 text-sm text-slate-800"
                        >
                            <span className="font-semibold text-indigo-700">{r.nama_vendor}:</span>{" "}
                            {r.ringkasan}
                        </li>
                    ))}
                </ul>
            )}
            {!data && !loading && !err && (
                <div className="text-center py-6 text-xs text-slate-400 italic">
                    Klik <b>Refresh Ringkasan</b> untuk mulai.
                </div>
            )}
        </div>
    );
}
